import { ref, computed } from 'vue';

/**
 * Levelling control centre — manage-fields state (shared).
 *
 * Owns which table fields are visible, their order, per-column width overrides
 * and the row density — persisted per user (PD-1120: field choices must survive
 * sessions). Field DEFINITIONS stay with the caller (they include portal
 * renderers and can grow asynchronously as hierarchy / classifier columns
 * arrive); this composable only manages keys against those defs.
 *
 * A field def needs at least: { key, label } and may set `locked` (always
 * visible, not toggleable), `minWidth`, and `pinAfter` / `pinBefore` (a sibling
 * key this column's position is fixed relative to — see safeOrder).
 *
 * Persistence is pluggable. By default prefs live in localStorage (per-user
 * key). Pass async `load`/`save` hooks to back them server-side instead (PD-1120
 * requires field choices persist per user server-side, not localStorage) — the
 * beta portal wires these to `authStore.getUserViewSetting/saveUserViewSetting`
 * and rm-portal to `loginState.getUserViewSetting/saveUserViewSetting`, both
 * hitting the shared `user_view_setting` table. localStorage then acts as an
 * instant cache (first paint) + offline fallback; a first-run migration pushes
 * any pre-existing local prefs up when the server has no record yet.
 *
 * @param {Object} options
 * @param {string}   options.storageKey     localStorage key (include the user id)
 * @param {Function} options.getFieldDefs   () => current field defs array
 * @param {Function} options.getBaseVisible () => keys visible by default, in order
 * @param {Function} [options.load]         async () => blob | null  (server read)
 * @param {Function} [options.save]         (blob) => void|Promise    (server write)
 */
export function levellingFieldPrefs(options) {
  const {
    storageKey, getFieldDefs, getBaseVisible, load, save,
    /* Bump when a column's DEFAULT visibility changes. See _reconcileDefaults. */
    defaultsRev = 0,
    /* Keys to reveal once on a defaultsRev bump. */
    getForceVisible = () => [],
  } = options;
  const serverBacked = typeof load === 'function' && typeof save === 'function';

  const self = {
    fieldOrder: ref([]),
    fieldOff: ref([]),
    colWidths: ref({}),
    density: ref('comfortable'),
    loading: ref(false),
    /* Which defaults revision the stored prefs were reconciled against. */
    rev: ref(0),
  };

  function _snapshotBlob() {
    return {
      order: self.fieldOrder.value,
      off: self.fieldOff.value,
      widths: self.colWidths.value,
      density: self.density.value,
      rev: self.rev.value,
    };
  }

  /* One-time reveal when a column's default visibility changes.
  
     The first time a user touches ANY column setting, the then-current hidden set —
     which includes the defaults from _baseOff() — is snapshotted into `fieldOff` as
     though they had chosen it. From then on the stored preference wins, and adding a
     key to the default-visible set can never reach them: hiddenKeys only ever ADDS
     to `off`, it never removes. So a changed default was invisible to precisely the
     users who had used the table.
  
     We cannot tell a deliberate hide from a frozen default by inspecting the blob,
     so the host declares a revision. On a bump we strip the named keys from `off`
     once and record the new revision; anything the user hides AFTERWARDS sticks. */
  function _reconcileDefaults() {
    if (!defaultsRev || self.rev.value >= defaultsRev) return false;
    const force = getForceVisible() || [];
    if (force.length) {
      self.fieldOff.value = self.fieldOff.value.filter((k) => !force.includes(k));
      /* Drop them from the stored ORDER as well. safeOrder honours the user's saved
         layout for any key it already contains, so a column whose DEFINED position
         has moved would otherwise stay where it used to sit. Removing it makes
         safeOrder treat it as newly added, which inserts it after the nearest
         preceding defined field — i.e. its designed position — while leaving the
         user's ordering of everything else untouched. */
      self.fieldOrder.value = self.fieldOrder.value.filter((k) => !force.includes(k));
    }
    self.rev.value = defaultsRev;
    // Always persist the advanced revision, or this re-runs on every load.
    return true;
  }

  function _apply(saved) {
    if (!saved || typeof saved !== 'object') return false;
    let any = false;
    if (Array.isArray(saved.order)) { self.fieldOrder.value = saved.order; any = true; }
    if (Array.isArray(saved.off)) { self.fieldOff.value = saved.off; any = true; }
    if (saved.widths && typeof saved.widths === 'object') { self.colWidths.value = saved.widths; any = true; }
    if (saved.density === 'compact' || saved.density === 'comfortable') { self.density.value = saved.density; any = true; }
    self.rev.value = Number(saved.rev) || 0;
    return any;
  }

  function _hasLocalPrefs() {
    return self.fieldOrder.value.length > 0 || self.fieldOff.value.length > 0
      || Object.keys(self.colWidths.value).length > 0 || self.density.value !== 'comfortable';
  }

  function _loadLocal() {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }

  function _persistLocal() {
    try {
      localStorage.setItem(storageKey, JSON.stringify(_snapshotBlob()));
    } catch (err) { /* storage unavailable — prefs just don't persist */ }
  }

  function _persist() {
    _persistLocal();
    if (serverBacked) {
      try {
        Promise.resolve(save(_snapshotBlob())).catch(() => {});
      } catch (err) { /* ignore */ }
    }
  }

  // Local cache first — instant paint for returning users, offline fallback.
  _apply(_loadLocal());
  // Reconcile the cached copy too, so the first paint already shows new defaults.
  _reconcileDefaults();

  // Server reconciliation: authoritative when a record exists; when there's no
  // record yet, migrate any pre-existing local prefs up once.
  async function _hydrate() {
    if (!serverBacked) return;
    self.loading.value = true;
    try {
      const serverBlob = await load();
      if (serverBlob && typeof serverBlob === 'object') {
        _apply(serverBlob);
        /* After the AUTHORITATIVE blob, so a stale revision on the server is
           reconciled and written back — not just patched locally. */
        const changed = _reconcileDefaults();
        _persistLocal();
        if (changed) await save(_snapshotBlob());
      } else if (_hasLocalPrefs()) {
        _reconcileDefaults();
        await save(_snapshotBlob());
      }
    } catch (err) {
      // Keep the local cache on any failure.
    } finally {
      self.loading.value = false;
    }
  }
  self.ready = _hydrate();

  self.byKey = computed(() => {
    const map = {};
    (getFieldDefs() || []).forEach((d) => { map[d.key] = d; });
    return map;
  });

  function _baseOrder() {
    const base = getBaseVisible() || [];
    const rest = (getFieldDefs() || []).map((d) => d.key).filter((k) => !base.includes(k));
    return base.concat(rest);
  }

  function _baseOff() {
    const base = new Set(getBaseVisible() || []);
    return (getFieldDefs() || [])
      .filter((d) => !base.has(d.key) && !d.locked)
      .map((d) => d.key);
  }

  // Saved order reconciled against the CURRENT defs: drop unknown keys, append
  // fields that didn't exist when the prefs were saved. New non-base fields
  // start hidden unless the user has an explicit preference.
  self.safeOrder = computed(() => {
    const byKey = self.byKey.value;
    const seen = new Set();
    const out = [];
    const source = self.fieldOrder.value.length ? self.fieldOrder.value : _baseOrder();
    source.forEach((k) => { if (byKey[k] && !seen.has(k)) { seen.add(k); out.push(k); } });
    /* Columns added since the prefs were saved. Appending them would drop a new
       column after the user's custom/added columns, which is never where it was
       designed to sit — so each one is inserted after the nearest PRECEDING field
       from the definition order that is already placed. Consecutive new fields
       chain correctly because each is in `out` by the time the next looks back.
       The user's own ordering of fields they have seen is never disturbed. */
    const defs = getFieldDefs() || [];
    defs.forEach((d, i) => {
      if (seen.has(d.key)) return;
      seen.add(d.key);
      let at = out.length;
      for (let j = i - 1; j >= 0; j--) {
        const idx = out.indexOf(defs[j].key);
        if (idx !== -1) { at = idx + 1; break; }
      }
      out.splice(at, 0, d.key);
    });
    /* PINNED columns: position fixed by the definition, not by the stored order.
    
       Needed because everything above deliberately honours the user's saved layout,
       which means a column whose defined position CHANGES can never move for anyone
       who already has prefs — their stored order keeps winning, on every load, and no
       amount of clearing the browser helps because the prefs are server-backed. A
       column marked `pinAfter` is one whose place is part of the design rather than a
       user preference, so it is repositioned every time and cannot drift.
    
       Deliberate trade-off: such a column can't be reordered by the user. Only set
       `pinAfter` where that is the intent. */
    defs.forEach((d) => {
      if (!d.pinAfter) return;
      const from = out.indexOf(d.key);
      if (from === -1) return;
      out.splice(from, 1);
      const after = out.indexOf(d.pinAfter);
      out.splice(after === -1 ? out.length : after + 1, 0, d.key);
    });
    /* `pinBefore` — the same contract, for a column whose designed place is immediately
       BEFORE another. Written for the hierarchy chain (family group → family → …):
       reading broad-to-specific is a property of the hierarchy, not a user choice, and
       only the most specific level is visible by default, so its parents would otherwise
       be listed far below it among the leftover hidden fields.

       Applied in REVERSE definition order so a CHAIN resolves outwards from whatever
       the chain is anchored to: the closest parent lands next to its child first, and
       each broader level then pins onto the level it precedes. Forwards, a parent would
       pin onto a child that had not moved into place yet. */
    for (let i = defs.length - 1; i >= 0; i--) {
      const d = defs[i];
      if (!d.pinBefore) continue;
      const from = out.indexOf(d.key);
      if (from === -1) continue;
      out.splice(from, 1);
      const before = out.indexOf(d.pinBefore);
      out.splice(before === -1 ? out.length : before, 0, d.key);
    }
    return out;
  });

  self.hiddenKeys = computed(() => {
    // No stored prefs yet → hide everything outside the base default.
    if (!self.fieldOrder.value.length && !self.fieldOff.value.length) return _baseOff();
    const byKey = self.byKey.value;
    const known = new Set(self.fieldOrder.value);
    const off = self.fieldOff.value.filter((k) => byKey[k]);
    // Fields added since the prefs were saved default to hidden (unless base).
    const base = new Set(getBaseVisible() || []);
    (getFieldDefs() || []).forEach((d) => {
      if (!known.has(d.key) && !base.has(d.key) && !d.locked && !off.includes(d.key)) off.push(d.key);
    });
    return off;
  });

  self.visibleFields = computed(() => {
    const byKey = self.byKey.value;
    const off = new Set(self.hiddenKeys.value);
    return self.safeOrder.value
      .map((k) => byKey[k])
      .filter((d) => d && (d.locked || !off.has(d.key)));
  });

  function _snapshot() {
    // Materialise the computed order/off before mutating, so first-time edits
    // start from the resolved defaults rather than empty arrays.
    //
    // ORDER MATTERS: read BOTH computeds first. `hiddenKeys` depends on
    // `fieldOrder` — assigning fieldOrder before reading hiddenKeys flips it out
    // of its "no stored prefs → hide non-base" branch, so it would resolve to []
    // (nothing hidden) and a single toggle would reveal every column. Snapshot
    // the resolved values, THEN assign.
    const order = [...self.safeOrder.value];
    const off = [...self.hiddenKeys.value];
    self.fieldOrder.value = order;
    self.fieldOff.value = off;
  }

  self.toggleField = (key) => {
    const def = self.byKey.value[key];
    if (!def || def.locked) return;
    _snapshot();
    const off = new Set(self.fieldOff.value);
    off.has(key) ? off.delete(key) : off.add(key);
    self.fieldOff.value = [...off];
    _persist();
  };

  self.moveField = (fromIndex, toIndex) => {
    _snapshot();
    const order = [...self.fieldOrder.value];
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= order.length) return;
    const [moved] = order.splice(fromIndex, 1);
    order.splice(toIndex, 0, moved);
    self.fieldOrder.value = order;
    _persist();
  };

  self.setWidth = (key, px) => {
    const def = self.byKey.value[key];
    const min = (def && def.minWidth) || 90;
    self.colWidths.value = { ...self.colWidths.value, [key]: Math.max(min, Math.round(px)) };
    _persist();
  };

  self.resetWidth = (key) => {
    const widths = { ...self.colWidths.value };
    delete widths[key];
    self.colWidths.value = widths;
    _persist();
  };

  self.setDensity = (density) => {
    self.density.value = density === 'compact' ? 'compact' : 'comfortable';
    _persist();
  };

  self.resetToDefault = () => {
    self.fieldOrder.value = [];
    self.fieldOff.value = [];
    self.colWidths.value = {};
    _persist();
  };

  return self;
}
